import React from 'react';
import { Button, Col, Row, Panel } from 'react-bootstrap';
import * as faIcon from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AscComponent from '../../components/AscComponent'
import AscModal from "../../containers/Elements/Modal/Modal";
import Favi from '../../containers/Elements/Favi/Favi';
import InboundGroupModal from '../../containers/InboundGroup/InboundGroupModal';
import ExternalNumberModal from '../../containers/ExternalNumber/ExternalNumberModal';
import ExtensionNumberModal from '../../containers/ExtensionNumber/ExtensionNumberModal';
import BusinessCalendarModal from '../../containers/BusinessCalendarV2/BusinessCalendarModal';
import BusinessCalendarType from '../../containers/BusinessCalendarV2/Type.json';
import { CSVLink } from "react-csv";
import moment from "moment";
import * as GlobalConst from "../../components/AscConstants";

/**
 * スクリプト管理専用の番号設定構成図コンポーネント（6列構成）
 * 外線番号 / 営業カレンダー / スクリプト / 着信ブロック / 内線グループ / 内線番号
 */
class ScriptNumberSettingInfoDiagram extends AscComponent {
    constructor(props) {
        super(props);
        this.state = {
            showExternalNumberModal: false,
            externalNumberModalLoading: false,
            showInboundGroupModal: false,
            inboundGroupModalLoading: false,
            showExtensionNumberModal: false,
            extensionNumberModalLoading: false,
            showBusinessCalendarModal: false,
            businessCalendarModalLoading: false,
            externalNumberModalData: null,
            inboundGroupModalData: null,
            extensionNumberModalData: null,
            businessCalendarModalData: null,
            csvData: [],
            csvHeader: [],
            csvLoading: false
        };
        this.csvLink = React.createRef();
    }

    componentDidMount = () => {
        this.scheduleDrawConnections();
    }

    componentDidUpdate = (prevProps) => {
        const { state } = this.props;
        const prevState = prevProps.state;
        if (state.showResourceMap && (
            (!prevState.showResourceMap) ||
            (state.resourceMapData !== prevState.resourceMapData) ||
            (prevState.resourceMapLoading && !state.resourceMapLoading)
        )) {
            this.scheduleDrawConnections();
        }
    }

    scheduleDrawConnections = () => {
        const { state } = this.props;
        const DRAW_CONNECTION_DELAY = 500;
        if (state.showResourceMap && !state.resourceMapLoading && state.resourceMapData) {
            setTimeout(() => {
                this.drawConnections();
            }, DRAW_CONNECTION_DELAY);
        }
    }

    drawConnections = () => {
        try {
            const { connectionLineClass } = this.props;
            const svg = document.querySelector(`.${connectionLineClass}`);
            const DRAW_CONNECTION_DELAY = 1000;
            if (!svg) {
                setTimeout(() => {
                    const retrySvg = document.querySelector(`.${connectionLineClass}`);
                    if (retrySvg) {
                        this.drawConnectionsInternal(retrySvg);
                    }
                }, DRAW_CONNECTION_DELAY);
                return;
            }
            this.drawConnectionsInternal(svg);
        } catch (error) {
            console.error(this.props.langText.Message.ScriptNumberSettingInfoDiagram.drawConnectionError, error);
        }
    }

    drawConnectionsInternal = (svg) => {
        try {
            while (svg.firstChild) {
                svg.removeChild(svg.firstChild);
            }
            const { tableClass } = this.props;
            const table = document.querySelector(`.${tableClass}`);
            if (!table) return;
            const svgRect = svg.getBoundingClientRect();
            this.adjustCalendarPositions();
            this.drawConnectionLines(svg, svgRect, table);
        } catch (error) {
            console.error(this.props.langText.Message.ScriptNumberSettingInfoDiagram.drawConnectionError, error);
        }
    }

    /**
     * 各カレンダーdivのY位置を、対応する外線番号グループの先頭divのY位置に動的に合わせる。
     * 外線番号ボックスの高さが可変（メモ有り/無しで2行になる等）のため、
     * 静的px計算ではなくDOM描画後の実際の位置（getBoundingClientRect）を使用する。
     */
    adjustCalendarPositions = () => {
        const calendarBoxes = Array.from(document.querySelectorAll('.script-diagram-calendar-box'));
        calendarBoxes.forEach((calBox) => {
            const calId = calBox.getAttribute('data-calendar-id');
            const extBoxes = Array.from(document.querySelectorAll(`.script-diagram-external-box[data-calendar-id="${calId}"]`));
            if (extBoxes.length === 0) return;

            const firstExtRect = extBoxes[0].getBoundingClientRect();
            const prevSibling = calBox.previousElementSibling;
            if (prevSibling) {
                // 前のカレンダーdivの下端から、対応する先頭外線divの上端まで
                const prevBottom = prevSibling.getBoundingClientRect().bottom;
                calBox.style.marginTop = `${Math.max(2, firstExtRect.top - prevBottom)}px`;
            } else {
                // 最初のカレンダーdiv: 親TD上端から先頭外線divの上端まで
                const parentRect = calBox.parentElement.getBoundingClientRect();
                calBox.style.marginTop = `${Math.max(0, firstExtRect.top - parentRect.top)}px`;
            }
        });
    }

    /**
     * 6列版接続線描画
     * 列順: 外線番号(col0) | 営業カレンダー(col1) | スクリプト(col2) | 着信ブロック(col3) | 内線グループ(col4) | 内線番号(col5)
     */
    drawConnectionLines = (svg, svgRect, table) => {
        // 列境界のX座標を取得
        let borderXList = [];
        try {
            const firstRow = table && table.querySelector('tbody tr');
            if (firstRow) {
                const tds = firstRow.children;
                for (let i = 0; i < tds.length - 1; i++) {
                    borderXList[i] = Math.round(tds[i].getBoundingClientRect().right - svgRect.left);
                }
            }
        } catch (e) { /* noop */ }

        const createLine = (x1, y1, x2, y2) => {
            const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            line.setAttribute('x1', Math.round(x1));
            line.setAttribute('y1', Math.round(y1));
            line.setAttribute('x2', Math.round(x2));
            line.setAttribute('y2', Math.round(y2));
            line.setAttribute('stroke', '#8e9ba7');
            line.setAttribute('stroke-width', '3');
            svg.appendChild(line);
        };

        const drawLShape = (startBox, endBoxes, borderX) => {
            if (!startBox || !endBoxes || endBoxes.length === 0) return;
            const startRect = startBox.getBoundingClientRect();
            const startX = Math.round(startRect.right - svgRect.left);
            const startY = Math.round(startRect.top + startRect.height / 2 - svgRect.top);

            endBoxes.forEach(endBox => {
                const endRect = endBox.getBoundingClientRect();
                const endX = Math.round(endRect.left - svgRect.left);
                const endY = Math.round(endRect.top + endRect.height / 2 - svgRect.top);
                const trunkX = borderX !== undefined ? borderX : Math.round((startX + endX) / 2);
                createLine(startX, startY, trunkX, startY);
                createLine(trunkX, startY, trunkX, endY);
                createLine(trunkX, endY, endX, endY);
            });
        };

        const drawLShapeReverse = (startBoxes, endBox, borderX) => {
            if (!endBox || !startBoxes || startBoxes.length === 0) return;
            const endRect = endBox.getBoundingClientRect();
            const endX = Math.round(endRect.left - svgRect.left);
            const endY = Math.round(endRect.top + endRect.height / 2 - svgRect.top);

            startBoxes.forEach(startBox => {
                const startRect = startBox.getBoundingClientRect();
                const startX = Math.round(startRect.right - svgRect.left);
                const startY = Math.round(startRect.top + startRect.height / 2 - svgRect.top);
                const trunkX = borderX !== undefined ? borderX : Math.round((startX + endX) / 2);
                createLine(startX, startY, trunkX, startY);
                createLine(trunkX, startY, trunkX, endY);
                createLine(trunkX, endY, endX, endY);
            });
        };

        // === 左方向: 外線番号(col0) ↔ 営業カレンダー(col1) ↔ スクリプト(col2) ===
        const scriptBox = document.querySelector('.script-diagram-script-box');

        // 営業カレンダー → スクリプト
        const calendarBoxes = Array.from(document.querySelectorAll('.script-diagram-calendar-box'));
        if (scriptBox && calendarBoxes.length > 0) {
            drawLShapeReverse(calendarBoxes, scriptBox, borderXList[1]);
        }

        // 外線番号 → 営業カレンダー
        calendarBoxes.forEach(calBox => {
            const calId = calBox.getAttribute('data-calendar-id');
            const extBoxes = Array.from(document.querySelectorAll(`.script-diagram-external-box[data-calendar-id="${calId}"]`));
            if (extBoxes.length > 0) {
                drawLShapeReverse(extBoxes, calBox, borderXList[0]);
            }
        });

        // === 右方向: スクリプト(col2) → 着信ブロック(col3) → 内線グループ(col4) → 内線番号(col5) ===
        const incomingBlockBoxes = Array.from(document.querySelectorAll('.script-diagram-block-box'));

        // スクリプト → 着信ブロック
        if (scriptBox && incomingBlockBoxes.length > 0) {
            drawLShape(scriptBox, incomingBlockBoxes, borderXList[2]);
        }

        // 着信ブロック → 内線グループ
        incomingBlockBoxes.forEach(blockBox => {
            const blockId = blockBox.getAttribute('data-block-id');
            const groupBoxes = Array.from(document.querySelectorAll(`.script-diagram-group-box[data-block-id="${blockId}"]`));
            if (groupBoxes.length > 0) {
                drawLShape(blockBox, groupBoxes, borderXList[3]);
            }
        });

        // 内線グループ → 内線番号
        const allGroupBoxes = Array.from(document.querySelectorAll('.script-diagram-group-box'));
        allGroupBoxes.forEach(groupBox => {
            const groupKey = groupBox.getAttribute('data-group-key');
            const extBoxes = Array.from(document.querySelectorAll(`.script-diagram-extension-box[data-group-key="${groupKey}"]`));
            if (extBoxes.length > 0) {
                drawLShape(groupBox, extBoxes, borderXList[4]);
            }
        });
    }


    // ===== 権限判定ヘルパー =====

    /**
     * 拠点/フロア管理者権限の判定
     * 会社スコープ以上: 常に表示
     * 拠点スコープ: floor_flg がある場合は親拠点プレフィックス一致、そうでなければ同一拠点
     * フロアスコープ: 同一拠点のみ
     */
    canSeeByDepartment = (department_id, department_prefix) => {
        const userInfo = this.props.userInfo || {};
        if (this.props.getScopeGreaterEqual(GlobalConst.SCOPE_OBJECT.company)) return true;
        if (this.props.getScopeGreaterEqual(GlobalConst.SCOPE_OBJECT.department)) {
            if (userInfo.floor_flg) {
                const myParentPrefix = (userInfo.department_prefix || '').slice(0, 2);
                const targetParentPrefix = (department_prefix || '').slice(0, 2);
                return myParentPrefix !== '' && myParentPrefix === targetParentPrefix;
            }
            return String(department_id) === String(userInfo.department_id);
        }
        if (this.props.getScopeGreaterEqual(GlobalConst.SCOPE_OBJECT.floor)) {
            return String(department_id) === String(userInfo.department_id);
        }
        return true;
    };

    // ===== テーブル描画 =====

    renderTableContent = ({ resourceMapData, tableClass, connectionLineClass }) => {
        const langText = this.props.langText;
        const { businessCalendars = [], incomingBlocks = [], scriptName } = resourceMapData;
        const userInfo = this.props.userInfo || {};

        // バージョン無し・着信ブロック0件の判定
        const hasIncomingBlocks = incomingBlocks.length > 0;

        // 行数計算（テーブルの総行数）
        // 外線番号・カレンダーはrowSpan=totalRowsの1TDに縦並べのため、右側（内線）の行数のみで決定する
        // 右側: 内線グループの合計数、さらに内線番号の合計数

        // 右側の行数を計算（着信ブロックがある場合）
        let rightRows = 1;
        if (hasIncomingBlocks) {
            rightRows = incomingBlocks.reduce((total, block) => {
                if (block.inboundGroups.length === 0) return total + 1;
                return total + block.inboundGroups.reduce((gTotal, group) => {
                    const canSee = this.canSeeByDepartment(group.department_id, group.department_prefix);
                    return gTotal + Math.max(1, canSee ? group.extensionNumbers.length : 0);
                }, 0);
            }, 0);
        }

        const totalRows = Math.max(rightRows, 1);

        // ヘッダー
        const renderHeader = () => (
            <thead>
                <tr>
                    <th className="number-setting-info-diagram-th number-setting-info-diagram-th--right-border script-number-setting-info-diagram-th">
                        {langText.Body.ScriptNumberSettingInfoDiagram.columnExternalNumber}
                    </th>
                    <th className="number-setting-info-diagram-th number-setting-info-diagram-th--right-border script-number-setting-info-diagram-th">
                        {langText.Body.ScriptNumberSettingInfoDiagram.columnBusinessCalendar}
                    </th>
                    <th className="number-setting-info-diagram-th number-setting-info-diagram-th--right-border script-number-setting-info-diagram-th">
                        {langText.Body.ScriptNumberSettingInfoDiagram.columnScript}
                    </th>
                    <th className="number-setting-info-diagram-th number-setting-info-diagram-th--right-border script-number-setting-info-diagram-th">
                        {langText.Body.ScriptNumberSettingInfoDiagram.columnIncomingBlock}
                    </th>
                    <th className="number-setting-info-diagram-th number-setting-info-diagram-th--right-border script-number-setting-info-diagram-th">
                        {langText.Body.ScriptNumberSettingInfoDiagram.columnInboundGroup}
                    </th>
                    <th className="number-setting-info-diagram-th script-number-setting-info-diagram-th">
                        {langText.Body.ScriptNumberSettingInfoDiagram.columnExtensionNumber}
                    </th>
                </tr>
            </thead>
        );

        // ボディ行を構築
        const rows = [];

        // 右側(着信ブロック・内線グループ・内線番号)の行を構築
        const rightRowData = [];
        if (!hasIncomingBlocks) {
            rightRowData.push({ blockId: null, blockNumber: null, groupId: null, groupName: null, groupKey: null, extensionNumber: null });
        } else {
            incomingBlocks.forEach(block => {
                if (block.inboundGroups.length === 0) {
                    rightRowData.push({
                        blockId: block.block_id,
                        blockNumber: block.block_number,
                        blockTitle: block.block_title || '',
                        groupId: null,
                        groupName: null,
                        groupKey: null,
                        groupCanSee: true,
                        extensionNumber: null
                    });
                } else {
                    block.inboundGroups.forEach((group, gIdx) => {
                        const groupKey = `${block.block_id}_${group.inbound_group_id}_${gIdx}`;
                        // ② 内線グループの閲覧権限判定
                        const groupCanSee = this.canSeeByDepartment(group.department_id, group.department_prefix);
                        if (!groupCanSee || group.extensionNumbers.length === 0) {
                            // 権限外グループはextensionNumberをnullで1行のみ
                            rightRowData.push({
                                blockId: block.block_id,
                                blockNumber: block.block_number,
                                blockTitle: block.block_title || '',
                                groupId: group.inbound_group_id,
                                groupName: group.inbound_group_name,
                                groupKey, groupCanSee,
                                extensionNumber: null
                            });
                        } else {
                            group.extensionNumbers.forEach(ext => {
                                rightRowData.push({
                                    blockId: block.block_id,
                                    blockNumber: block.block_number,
                                    blockTitle: block.block_title || '',
                                    groupId: group.inbound_group_id,
                                    groupName: group.inbound_group_name,
                                    groupKey, groupCanSee,
                                    extensionNumber: ext
                                });
                            });
                        }
                    });
                }
            });
        }

        // 行を生成
        for (let i = 0; i < totalRows; i++) {
            const right = rightRowData[i] || { blockId: null, blockNumber: null, groupId: null, groupName: null, groupKey: null, extensionNumber: null };
            const isFirstRow = i === 0;

            // 右側: ブロックIDが変わったら新しいブロックセル
            const isFirstOfBlock = right.blockId !== null && (i === 0 || rightRowData[i - 1].blockId !== right.blockId);
            const blockRowSpan = right.blockId !== null
                ? rightRowData.filter(r => r.blockId === right.blockId).length
                : 1;

            // 右側: groupKeyが変わったらグループセル
            const isFirstOfGroup = right.groupKey !== null && (i === 0 || rightRowData[i - 1].groupKey !== right.groupKey);
            const groupRowSpan = right.groupKey !== null
                ? rightRowData.filter(r => r.groupKey === right.groupKey).length
                : 1;

            rows.push(
                <tr key={`row-${i}`}>
                    {/* 外線番号列 - isFirstRowのみ、全外線番号をdivで縦並べ（行高さ影響を排除）
                         カレンダーグループ間のmarginTopを揃えることで対応関係を視覚化 */}
                    {isFirstRow ? (
                        <td rowSpan={totalRows} className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border script-number-setting-info-diagram-td" style={{ padding: '2px 10px', verticalAlign: 'top' }}>
                            {businessCalendars.flatMap((cal, calIdx) => {
                                const visibleExternals = cal.externalNumbers.filter(ext => {
                                    if (this.props.getScopeGreaterEqual(GlobalConst.SCOPE_OBJECT.company)) return true;
                                    if (!ext.departments || ext.departments.length === 0) return false;
                                    return ext.departments.some(dept => this.canSeeByDepartment(dept.department_id, dept.department_prefix));
                                });
                                return visibleExternals.map((ext, extIdx) => {
                                    // カレンダーグループの最初の外線番号に大きめのmarginTopを設定
                                    // （カレンダーTDの区切りmarginTopと揃えることで対応関係を視覚化）
                                    const marginTop = calIdx === 0 && extIdx === 0 ? '6px'
                                        : extIdx === 0 ? '16px'
                                        : '2px';
                                    return (
                                        <div
                                            key={`ext-${calIdx}-${extIdx}`}
                                            className="script-diagram-external-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide minh-50 font-weight-bold text-14-px number-setting-info-clickable"
                                            data-external-id={ext.external_number_id}
                                            data-calendar-id={cal.calendar_id}
                                            onClick={event => this.handleExternalClick(event, ext.external_number_id)}
                                            style={{ cursor: 'pointer', marginTop }}
                                        >
                                            <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                                <FontAwesomeIcon className="sidebar-icon fa-fw" icon={faIcon.faFax} size="lg" style={{ flexShrink: 0 }} />
                                                <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                                    <div className="truncate-ellipsis">{ext.display_number}</div>
                                                    {ext.memo && (
                                                        <div className="text-12-px margin-top-2-px op-90 truncate-ellipsis" title={ext.memo}>
                                                            ({ext.memo})
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                });
                            })}
                        </td>
                    ) : null}

                    {/* 営業カレンダー列 - isFirstRowのみ、全カレンダーをdivで縦並べ（行高さ影響を排除）
                         各カレンダーdivのY位置はadjustCalendarPositions()でDOM描画後に動的調整する */}
                    {isFirstRow ? (
                        <td rowSpan={totalRows} className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border script-number-setting-info-diagram-td" style={{ padding: '2px 10px', verticalAlign: 'top' }}>
                            {businessCalendars.map((cal, calIdx) => {
                                return cal.calendar_name ? (
                                    <div
                                        key={`cal-${calIdx}`}
                                        className="script-diagram-calendar-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide minh-50 font-weight-bold text-14-px number-setting-info-clickable"
                                        data-calendar-id={cal.calendar_id}
                                        onClick={event => this.handleCalendarClick(event, cal.calendar_id)}
                                        style={{ cursor: 'pointer', marginTop: calIdx === 0 ? '6px' : '2px' }}
                                    >
                                        <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                            <FontAwesomeIcon className="sidebar-icon fa-fw" icon={faIcon.faCalendarAlt} size="lg" style={{ flexShrink: 0 }} />
                                            <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                                <div className="truncate-ellipsis" title={cal.calendar_name}>{cal.calendar_name}</div>
                                            </div>
                                        </div>
                                    </div>
                                ) : null;
                            })}
                        </td>
                    ) : null}

                    {/* スクリプト列（全行rowspan） */}
                    {isFirstRow && (
                        <td
                            rowSpan={totalRows}
                            className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border script-number-setting-info-diagram-td"
                            style={{ padding: '2px 10px', verticalAlign: 'top' }}
                        >
                            <div
                                className="script-diagram-script-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide minh-50 font-weight-bold text-14-px"
                                style={{ marginTop: '6px' }}
                            >
                                <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                    <FontAwesomeIcon className="sidebar-icon fa-fw" icon={faIcon.faRandom} size="lg" style={{ flexShrink: 0 }} />
                                    <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                        <div className="truncate-ellipsis" title={scriptName}>{scriptName}</div>
                                    </div>
                                </div>
                            </div>
                        </td>
                    )}

                    {/* 着信ブロック列（着信ブロックがある場合はボックス表示、ない場合は空列） */}
                    {hasIncomingBlocks && isFirstOfBlock && right.blockId !== null ? (
                        <td
                            rowSpan={blockRowSpan}
                            className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border script-number-setting-info-diagram-td"
                            style={{ padding: '2px 10px', verticalAlign: 'top' }}
                        >
                            <div
                                className="script-diagram-block-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide minh-50 font-weight-bold text-14-px"
                                data-block-id={right.blockId}
                                style={{ marginTop: isFirstRow ? '6px' : '2px' }}
                            >
                                <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                    <FontAwesomeIcon className="sidebar-icon fa-fw" icon={faIcon.faPhone} size="lg" style={{ flexShrink: 0 }} />
                                    <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                        <div className="truncate-ellipsis" title={right.blockTitle ? `#${right.blockNumber} ${right.blockTitle}` : `#${right.blockNumber}`}>{right.blockTitle ? `#${right.blockNumber} ${right.blockTitle}` : `#${right.blockNumber}`}</div>
                                    </div>
                                </div>
                            </div>
                        </td>
                    ) : hasIncomingBlocks && !isFirstOfBlock && right.blockId !== null ?
                            // 同ブロックの rowspan 継続行の場合、null
                            null :
                            (hasIncomingBlocks && right.blockId === null && !isFirstRow ?
                                // 着信ブロックデータが無い場合、空のtdを設定
                                (
                                    <td className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border script-number-setting-info-diagram-td" style={{ padding: '2px 10px' }} />
                                ) : isFirstRow ?
                                    // 着信ブロックが0個である場合、1列を丸ごと空のtdで埋める
                                    (
                                        <td rowSpan={totalRows} className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border script-number-setting-info-diagram-td" style={{ padding: '2px 10px' }} />
                                    ) : null
                            )
                    }

                    { /* 内線グループ列 */}
                    {hasIncomingBlocks && isFirstOfGroup && right.groupId !== null ? (
                        <td
                            rowSpan={groupRowSpan}
                            className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border script-number-setting-info-diagram-td"
                            style={{ padding: '2px 10px', verticalAlign: 'top' }}
                        >
                            <div
                                className={`script-diagram-group-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide minh-50 font-weight-bold text-14-px ${right.groupCanSee !== false ? 'number-setting-info-clickable' : 'number-setting-info-is-disabled'}`}
                                data-block-id={right.blockId}
                                data-group-id={right.groupId}
                                data-group-key={right.groupKey}
                                onClick={right.groupCanSee !== false ? (event => this.handleInboundGroupClick(event, right.groupId)) : undefined}
                                style={{ cursor: right.groupCanSee !== false ? 'pointer' : 'default', marginTop: isFirstRow ? '6px' : '2px' }}
                            >
                                <div className="number-setting-info-row-center-gap-8" style={{ minWidth: 0 }}>
                                    <FontAwesomeIcon className="sidebar-icon fa-fw" icon={faIcon.faUsers} size="lg" style={{ flexShrink: 0 }} />
                                    <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                        <div className="truncate-ellipsis" title={right.groupName}>{right.groupName}</div>
                                    </div>
                                </div>
                            </div>
                        </td>
                    ) : hasIncomingBlocks && !isFirstOfGroup && right.groupId !== null ?
                            // グループの rowspan 継続行の場合、null
                            null :
                                (hasIncomingBlocks && right.groupId === null ?
                                    // 内線グループが紐づいていない着信ブロックの行。グループ列にデータがないため空のtdを設定
                                    (
                                        <td className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border script-number-setting-info-diagram-td" style={{ padding: '2px 10px' }} />
                                    ) : !hasIncomingBlocks && isFirstRow ?
                                        // 着信ブロックが0個である場合、1列を丸ごと空のtdで埋める
                                        (
                                            <td rowSpan={totalRows} className="number-setting-info-diagram-td number-setting-info-diagram-td--right-border script-number-setting-info-diagram-td" style={{ padding: '2px 10px' }} />
                                        ) : null
                                )
                    }

                    {/* 内線番号列 */}
                    {hasIncomingBlocks ? (
                        right.groupId !== null ? (
                            isFirstOfGroup ? (
                                // グループ最初の行のみTDを生成し、グループ内の全内線番号をまとめて縦に並べる
                                // → 外線番号列など他の列の行高さ影響を受けず、均等な間隔になる
                                // (「1行=1内線番号セル」だと、外線番号列の行の高さに表示が影響してしまう)
                                <td
                                    rowSpan={groupRowSpan}
                                    className="number-setting-info-diagram-td script-number-setting-info-diagram-td"
                                    style={{ padding: '2px 10px', verticalAlign: 'top' }}
                                >
                                    {/* ② groupCanSee=falseの場合は内線番号を表示しない */}
                                    {right.groupCanSee !== false && rightRowData
                                        .filter(r => r.groupKey === right.groupKey && r.extensionNumber !== null)
                                        .map((r, idx) => (
                                            <div
                                                key={`ext-${idx}`}
                                                className="script-diagram-extension-box number-setting-info-box-base number-setting-info-box-center number-setting-info-box-wide number-setting-info-clickable number-setting-info-box-compact font-weight-bold"
                                                data-extension-id={r.extensionNumber.extension_number_id}
                                                data-group-key={r.groupKey}
                                                onClick={event => this.handleExtensionNumberClick(event, r.extensionNumber.extension_number_id)}
                                                style={{ cursor: 'pointer', marginTop: idx === 0 ? '4px' : '2px', marginBottom: '2px' }}
                                            >
                                                <div className="number-setting-info-row-center-gap-6 text-14-px" style={{ minWidth: 0, gap: r.extensionNumber.type === 'softphone' ? '0.5px' : '6px' }}>
                                                    <Favi
                                                        className="sidebar-icon fa-fw"
                                                        icon={this.getExtensionIcon(r.extensionNumber.type)}
                                                        style={{ fontSize: r.extensionNumber.type === 'softphone' ? '11px' : '14px', flexShrink: 0 }}
                                                    />
                                                    <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                                        <div
                                                            className="truncate-ellipsis"
                                                            title={`${r.extensionNumber.unique_extension_number || r.extensionNumber.extension_number}${r.extensionNumber.memo ? ` (${r.extensionNumber.memo})` : ''}`}
                                                        >
                                                            {(r.extensionNumber.unique_extension_number || r.extensionNumber.extension_number)}
                                                            {r.extensionNumber.memo ? ` (${r.extensionNumber.memo})` : ''}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </td>
                            ) : null  // グループ継続行はrowspanのためnull
                        ) : (
                            // 内線グループなし（groupId === null）
                            right.blockId !== null ? (
                                isFirstOfBlock ? (
                                    <td
                                        rowSpan={blockRowSpan}
                                        className="number-setting-info-diagram-td script-number-setting-info-diagram-td"
                                        style={{ padding: '2px 10px' }}
                                    />
                                ) : null  // blockRowspan内の継続行はTD不要
                            ) : (
                                // right.blockId === null: leftRows > rightRows の余分な行
                                // TDが存在しないとテーブルに穴が開きPanelの背景色が透けるため空のTDを生成
                                <td className="number-setting-info-diagram-td script-number-setting-info-diagram-td" style={{ padding: '2px 10px' }} />
                            )
                        )
                    ) : isFirstRow ? (
                        <td rowSpan={totalRows} className="number-setting-info-diagram-td script-number-setting-info-diagram-td" style={{ padding: '2px 10px' }} />
                    ) : null}
                </tr>
            );
        }

        // スペーサー行
        rows.push(
            <tr key="spacer-row">
                <td className="number-setting-info-spacer-cell number-setting-info-spacer-cell--right-border script-number-setting-info-spacer-cell" />
                <td className="number-setting-info-spacer-cell number-setting-info-spacer-cell--right-border script-number-setting-info-spacer-cell" />
                <td className="number-setting-info-spacer-cell number-setting-info-spacer-cell--right-border script-number-setting-info-spacer-cell" />
                <td className="number-setting-info-spacer-cell number-setting-info-spacer-cell--right-border script-number-setting-info-spacer-cell" />
                <td className="number-setting-info-spacer-cell number-setting-info-spacer-cell--right-border script-number-setting-info-spacer-cell" />
                <td className="number-setting-info-spacer-cell script-number-setting-info-spacer-cell" />
            </tr>
        );

        return (
            <div className="number-setting-info-diagram-wrapper">
                <table className={`${tableClass} number-setting-info-diagram-table`}>
                    {renderHeader()}
                    <tbody>{rows}</tbody>
                </table>
                <svg className={`${connectionLineClass} number-setting-info-diagram-connection-overlay`} xmlns="http://www.w3.org/2000/svg" />
            </div>
        );
    }

    fetchExternalNumberDetail = async (externalId) => {
        try {
            const reqBody = {
                start: 0, length: 10,
                search: { value: '' },
                columns: [], order: [{ column: 0, dir: 'desc' }],
                sorted: [{ id: 'id', desc: true }],
                filtered: [{ id: 'id', value: externalId }]
            };
            const response = await this.props.ascAxios('post', 'ExternalNumber/board', reqBody);
            if (response.data && response.data.rows && Array.isArray(response.data.rows) && response.data.rows.length > 0) {
                const targetExternal = response.data.rows.find(row => row.id === parseInt(externalId));
                if (targetExternal) {
                    return targetExternal;
                } else {
                    console.warn(this.props.langText.Message.ScriptNumberSettingInfoDiagram.specifiedDisplayNumberDoesNotFound + externalId);
                    return null;
                }
            } else {
                return null;
            }
        } catch (error) {
            console.error(this.props.langText.Message.ScriptNumberSettingInfoDiagram.specifiedDisplayNumberInfoGetError, error);
            throw error;
        }
    }

    showExternalNumberModal = async (externalNumberData) => {
        try {
            let setData = {
                inboundTypeSelect: this.getSelectOption('external_inbound_group_type', this.props.langText.SelectOption),
                voice_log_expire_external_number_select: this.getSelectOption("voice_log_expire_external_number", this.props.langText.SelectOption),
                UnconnectedActionSelect: this.getSelectOption('unconnected_action', this.props.langText.SelectOption),
                inboundGroupSelect: [],
                inboundActionSelect: this.getSelectOption('inbound_action', this.props.langText.SelectOption),
                callOverActionSelect: this.getSelectOption('call_over_action', this.props.langText.SelectOption),
                callOverActionControlSelect: this.getSelectOption('call_over_action_control', this.props.langText.SelectOption),
                timeoutPlaybackSelect: this.getSelectOption('timeout_playback', this.props.langText.SelectOption),
                DialRestrictionSelect: this.getSelectOption('dial_restriction', this.props.langText.SelectOption),
                anonymousActionSelect: this.getSelectOption('anonymous_action', this.props.langText.SelectOption)
            };

            if (externalNumberData) {
                setData.external_id = externalNumberData.id || "";
                setData.gateway_id = externalNumberData.cm91_id ? externalNumberData.cm91_id : '';
                setData.gateway_name = externalNumberData.cm91_gateway_infos?.gw_name || '';
                setData.company_id = externalNumberData.cm12_companies?.id || "";
                setData.company_name = externalNumberData.cm12_companies?.company_name || "";

                try {
                    const gwRes = await this.props.ascAxios('post', 'Common/companyBygatewaySelect', { company_id: setData.company_id });
                    setData.gatewaySelect = gwRes.data || [];
                } catch (e) { setData.gatewaySelect = []; }

                try {
                    const ltRes = await this.props.ascAxios('post', 'Common/getLineTypes', { parameterId: 'LINE_TYPE' });
                    setData.lineTypeSelect = ltRes.data || [];
                } catch (e) { setData.lineTypeSelect = []; }

                setData.display_number = externalNumberData.display_number || "";
                setData.back_number = await this.getBackNumbers(externalNumberData.id);
                setData.channel_number = externalNumberData.channel_number !== "0" ? externalNumberData.channel_number : "";
                setData.memo = externalNumberData.memo || "";
                setData.description = externalNumberData.description || "";
                setData.wait_number = (externalNumberData.wait_number && externalNumberData.wait_number >= 0) || externalNumberData.wait_number === 0 ? externalNumberData.wait_number : "";
                setData.waitCallTimeoutSeconds = externalNumberData.wait_call_timeout_sec || "";

                const departmentInfo = Array.isArray(externalNumberData.ct62_assgin_external_department_infos) && externalNumberData.ct62_assgin_external_department_infos.length > 0
                    ? externalNumberData.ct62_assgin_external_department_infos[0] : null;
                const departmentInfos = externalNumberData.ct62_assgin_external_department_infos
                    ? externalNumberData.ct62_assgin_external_department_infos.map(row => ({
                        id: row.id, cm61_id: row.cm61_id, cm13_id: row.cm13_departments.id,
                        department_id: row.cm13_departments.id, parent_department_id: row.cm13_departments.parent_department_id,
                        floor_id: row.cm13_departments.floor_id, prefix: row.prefix, full_prefix: row.full_prefix,
                        department_name: row.cm13_departments.department_name, floor_name: row.cm13_departments.floor_name,
                        prefix_duplicationflag: false
                    })) : [];
                setData.departmentInfos = departmentInfos;
                setData.department_id = departmentInfo?.cm13_departments?.id || "";
                setData.department_name = departmentInfo?.cm13_departments?.department_name || "";
                setData.floor_flg = externalNumberData.cm12_companies?.floor_flg === "Y";
                setData.prefix = externalNumberData.sequence_number || "";

                setData.inbound_type = externalNumberData.inbound_group_type || "";
                setData.inbound_group_id = externalNumberData.ct29_assign_external_number_extension_group_infos?.cm28_inbound_group_infos
                    ? externalNumberData.ct29_assign_external_number_extension_group_infos.cm28_inbound_group_infos.map(row => row.id) : [];
                setData.inbound_group_name = externalNumberData.ct29_assign_external_number_extension_group_infos?.cm28_inbound_group_infos
                    ? externalNumberData.ct29_assign_external_number_extension_group_infos.cm28_inbound_group_infos.map(row => row.inbound_group_name) : [];

                try {
                    const res = await this.props.ascAxios("post", "/ExternalNumber/inboundGroupInfoSelect", {
                        department_id: departmentInfos.map(row => row.cm13_id),
                        inbound_type: externalNumberData.inbound_group_type
                    });
                    setData.inbound_group_cm51ids = res.data.cm28_extensions_ids;
                } catch (e) { setData.inbound_group_cm51ids = []; }

                setData.sound_id = externalNumberData.cm35_id !== "0" ? externalNumberData.cm35_id : "";
                setData.call_over_sound_id = externalNumberData.cm35_id_call_over_sound || "";
                setData.before_dial_sound_id = externalNumberData.cm35_id_before_dial || "";
                setData.cm35_id_timeout_sound = externalNumberData.cm35_id_timeout_sound || "";
                setData.unconnected_sound_id = externalNumberData.cm35_id_unconnected_sound || "";
                setData.anonymous_sound_id = externalNumberData.cm35_id_anonymous_sound || "";
                setData.wait_call_timeout_sound_id = externalNumberData.cm35_id_wait_call_timeout_sound || "";
                setData.waitCallTimeoutSoundId = externalNumberData.cm35_id_wait_call_timeout_sound || "";

                setData.business_calendar_id = externalNumberData.cm31_business_calendar_infos?.id || "";
                setData.business_calendar_name = externalNumberData.cm31_business_calendar_infos?.business_calendar_name || "";

                setData.callAnalysisFlag = !!(externalNumberData.cc81_setting_call_quality_external_number && externalNumberData.cc81_setting_call_quality_external_number.parameter_value === 1);
                setData.callEvalFlag = !!(externalNumberData.cc82_setting_call_eval_external_number && externalNumberData.cc82_setting_call_eval_external_number.parameter_value === 1);
                setData.fastOptionFlag = !!(externalNumberData.cc81_setting_call_quality_external_number && externalNumberData.cc81_setting_call_quality_external_number.fast_option === "Y");

                setData.schedule_extension_group_infos = externalNumberData.ct30_assign_external_number_schedule_extension_group_infos || [];
                setData.schedule_wait_call_infos = externalNumberData.ct32_assign_external_number_schedule_wait_call_infos || [];
                setData.schedule_over_wait_call_infos = externalNumberData.ct33_assign_external_number_schedule_over_wait_call_infos || [];
                setData.schedule_dispensing_infos = externalNumberData.ct31_assign_external_number_schedule_dispensing_infos || [];

                setData.acd_flag = externalNumberData.acd_flag === "Y";
                setData.abj_flag = externalNumberData.abj_flag === "Y";
                setData.before_dial_flag = externalNumberData.before_dial_flag === "Y";
                setData.waitCallTimeoutUseFlag = externalNumberData.wait_call_timeout_flag === "Y";
                setData.call_history_delete_flag = externalNumberData.call_history_delete_flag === "Y";
                setData.voice_log_delete_flag = externalNumberData.voice_log_delete_flag === "Y";

                setData.timeout_sec = externalNumberData.timeout_sec || "";
                setData.call_over_action = externalNumberData.call_over_action || "";
                setData.call_over_action_control = externalNumberData.call_over_action_control || "";
                setData.timeout_action = externalNumberData.timeout_action ? externalNumberData.timeout_action : externalNumberData.timeout_playback_flag === "Y" ? "playback" : "unset";
                setData.unconnected_action = externalNumberData.unconnected_action || "";
                setData.anonymous_action = externalNumberData.anonymous_action || 'unset';
                setData.line_type = externalNumberData.line_type || "";
                setData.voice_log_expire_external_number =
                    (externalNumberData.voice_log_expire_external_number || externalNumberData.voice_log_expire_external_number === 0)
                        ? externalNumberData.voice_log_expire_external_number
                        : -1;
                setData.wait_call_timeout_sec = externalNumberData.wait_call_timeout_sec || "";
                setData.call_results = externalNumberData.ct602_assign_call_results ? externalNumberData.ct602_assign_call_results.map(row => row.cm64_id) : [];
                setData.omni_phone_back_ground_color = externalNumberData.omni_phone_back_ground_color ? externalNumberData.omni_phone_back_ground_color : GlobalConst.omni_phone_back_ground_color;
                setData.omni_phone_font_color = externalNumberData.omni_phone_font_color ? externalNumberData.omni_phone_font_color : GlobalConst.omni_phone_font_color;
                setData.before_omni_phone_back_ground_color = externalNumberData.omni_phone_back_ground_color ? externalNumberData.omni_phone_back_ground_color : '';
                setData.before_omni_phone_font_color = externalNumberData.omni_phone_font_color ? externalNumberData.omni_phone_font_color : '';
                setData.dial_restriction_flag = externalNumberData.dial_restriction_flag ? externalNumberData.dial_restriction_flag : "N";
                setData.listAppointInboundFlag = externalNumberData.cm76_autocall_list && externalNumberData.cm76_autocall_list.id;
                setData.list_out_action = externalNumberData.list_out_action ? externalNumberData.list_out_action : '';
                setData.list_name = externalNumberData.cm76_autocall_list ? externalNumberData.cm76_autocall_list.list_name : '';
                setData.list_sound_id = externalNumberData.cm35_id_list_sound ? externalNumberData.cm35_id_list_sound : '';
                setData.dispensing_time = externalNumberData.dispensing_time || "";
                setData.dispensing_count = externalNumberData.dispensing_count || "";
                setData.dispensing_flag = !!(externalNumberData.dispensing_time || externalNumberData.dispensing_count);
            }

            if (externalNumberData.cm12_companies?.id) {
                try {
                    const resCompanyInfo = await this.props.ascAxios('post', "/ExternalNumber/getItemsOnCompanySelected", {
                        company_id: externalNumberData.cm12_companies.id,
                        langtext_update: this.props.langText.Body.VoiceListUpdate,
                        langtext_create: this.props.langText.Body.VoiceListCreate
                    });
                    setData.waitNumberSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.listOutSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.beforeDialSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.listSelect = resCompanyInfo.data.cm76_autocall_list;
                    setData.callOverSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.UnconnectedSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.timeoutSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.anonymousSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.waitCallTimeoutSoundSelect = resCompanyInfo.data.cm35_voice_file_infos;
                    setData.callResultsSelect = resCompanyInfo.data.cm64_call_results;
                    setData.call_history_delete_flag_company = resCompanyInfo.data && resCompanyInfo.data.cm12_companies && resCompanyInfo.data.cm12_companies.call_history_delete_flag && resCompanyInfo.data.cm12_companies.call_history_delete_flag === "Y" ? true : false;
                    setData.voice_log_delete_flag_company = resCompanyInfo.data && resCompanyInfo.data.cm12_companies && resCompanyInfo.data.cm12_companies.voice_log_delete_flag && resCompanyInfo.data.cm12_companies.voice_log_delete_flag === "Y" ? true : false;

                    const resCompanyControlParam = await this.props.ascAxios('post', "/ExternalNumber/getCompanyControlParameter", { company_id: externalNumberData.cm12_companies.id });
                    setData.addingOptionCallAnalysis = resCompanyControlParam.data.paramValueCallAnalysis === "N" ? false : true;
                    setData.addingFastOption = resCompanyControlParam.data.paramValueFastOption === "ascend_whisper" ? true : false;
                    setData.addingOptionACD = resCompanyControlParam.data.paramValueACD === "N" ? false : true;
                    setData.addingOptionCallEval = resCompanyControlParam.data.paramValueCallEval === "N" ? false : true;
                    setData.addingOptionCallResult = resCompanyControlParam.data.paramValueCallResult === "N" ? false : true;
                } catch (e) { /* 処理継続 */ }
            }

            setData.showExternalNumberModal = true;
            setData.show = true;
            setData.showConfirm = false;
            setData.modalType = "read";

            this.setState({
                externalNumberModalData: setData,
                showExternalNumberModal: true,
                externalNumberModalLoading: false,
                showInboundGroupModal: false,
                showExtensionNumberModal: false,
                inboundGroupModalLoading: false,
                extensionNumberModalLoading: false
            });
        } catch (error) {
            this.setState({ showExternalNumberModal: false, externalNumberModalLoading: false });
        }
    }

    handleExternalClick = async (event, externalId) => {
        if (event) event.stopPropagation();
        if (!externalId) return;
        this.setState({ externalNumberModalLoading: true, showExternalNumberModal: false, showInboundGroupModal: false, showExtensionNumberModal: false });
        try {
            const data = await this.fetchExternalNumberDetail(externalId);
            if (data) {
                await this.showExternalNumberModal(data);
            } else {
                this.setState({ externalNumberModalLoading: false, showExternalNumberModal: false });
            }
        } catch (error) {
            this.setState({ externalNumberModalLoading: false, showExternalNumberModal: false });
            this.showErrorObjectMesssage(error);
        }
    }

    closeExternalNumberModal = () => {
        this.setState({ showExternalNumberModal: false, externalNumberModalLoading: false, externalNumberModalData: null });
    }

    fetchInboundGroupDetail = async (groupId) => {
        try {
            const reqBody = {
                start: 0, length: 10,
                search: { value: '' },
                columns: [], order: [{ column: 0, dir: 'desc' }],
                sorted: [{ id: 'id', desc: true }],
                filtered: [{ id: 'id', value: [parseInt(groupId)] }]
            };
            const response = await this.props.ascAxios('post', 'InboundGroup/board', reqBody);
            if (response.data && response.data.rows && response.data.rows.length > 0) {
                return response.data.rows[0];
            } else {
                return null;
            }
        } catch (error) {
            console.error(this.props.langText.Message.ScriptNumberSettingInfoDiagram.inboundGroupDetailGetError, error);
            throw error;
        }
    }

    showInboundGroupModal = async (inboundGroupData) => {
        try {
            let setData = {
                inboundGroupTypeSelect: this.getSelectOption('inbound_group_type', this.props.langText.SelectOption)
            };

            if (inboundGroupData) {
                setData.inbound_group_info_id = inboundGroupData.id || "";
                setData.company_id = inboundGroupData.cm13_departments?.cm12_companies?.id || "";
                setData.company_name = inboundGroupData.cm13_departments?.cm12_companies?.company_name || "";
                setData.floor_flg = inboundGroupData.cm13_departments?.cm12_companies?.floor_flg === "Y";
                if (setData.floor_flg) {
                    setData.floor_id = inboundGroupData.cm13_departments?.id || "";
                    setData.floor_name = inboundGroupData.cm13_departments?.floor_name || "";
                }
                setData.department_id = inboundGroupData.cm13_departments?.id || "";
                setData.department_name = inboundGroupData.cm13_departments?.department_name || "";
                setData.group_name = inboundGroupData.inbound_group_name || "";
                setData.group_type = inboundGroupData.inbound_type || setData.group_type;
                setData.group_type_org = inboundGroupData.inbound_type || setData.group_type;
                setData.extension_number = inboundGroupData.cm51_extension_numbers
                    ? inboundGroupData.cm51_extension_numbers.sort((a, b) => {
                        const an = a.unique_extension_number || a.extension_number || '';
                        const bn = b.unique_extension_number || b.extension_number || '';
                        return an.localeCompare(bn);
                    }).map(row => row.id) : [];
                setData.extensionNumberSelect = inboundGroupData.cm51_extension_numbers
                    ? inboundGroupData.cm51_extension_numbers.sort((a, b) => {
                        const an = a.unique_extension_number || a.extension_number || '';
                        const bn = b.unique_extension_number || b.extension_number || '';
                        return an.localeCompare(bn);
                    }).map(row => {
                        const numberStr = row.unique_extension_number || row.extension_number || '';
                        const normalizedNumber = numberStr.replace(/[a-zA-Z]+$/, '');
                        const nameStr = `(${row.memo || ''})`;
                        const labelText = `${normalizedNumber} ${nameStr}`;
                        const labeldata = React.createElement('div', null, [
                            labelText,
                            React.createElement(Favi, { key: 'icon', className: 'pull-right fa-fw fa-icon fa-icon-static', icon: row.type })
                        ]);
                        return { value: row.id, label: labeldata, search: `${normalizedNumber} ${row.memo || ''}`.trim(), extension_number: normalizedNumber, extension_name: row.memo || '', type: row.type || 'normal' };
                    }) : [];
            }

            setData.showInboundGroupModal = true;
            setData.show = true;
            setData.showConfirm = false;
            setData.modalType = "read";

            this.setState({
                inboundGroupModalData: setData,
                showInboundGroupModal: true,
                inboundGroupModalLoading: false,
                showExternalNumberModal: false,
                showExtensionNumberModal: false,
                externalNumberModalLoading: false,
                extensionNumberModalLoading: false
            });
        } catch (error) {
            this.setState({ showInboundGroupModal: false, inboundGroupModalLoading: false });
        }
    }

    handleInboundGroupClick = async (event, groupId) => {
        if (event) event.stopPropagation();
        if (!groupId) return;
        this.setState({ inboundGroupModalLoading: true, showInboundGroupModal: false, showExternalNumberModal: false, showExtensionNumberModal: false });
        try {
            const data = await this.fetchInboundGroupDetail(groupId);
            if (data) {
                await this.showInboundGroupModal(data);
            } else {
                this.setState({ inboundGroupModalLoading: false, showInboundGroupModal: false });
            }
        } catch (error) {
            this.setState({ inboundGroupModalLoading: false, showInboundGroupModal: false });
            this.showErrorObjectMesssage(error);
        }
    }

    closeInboundGroupModal = () => {
        this.setState({ showInboundGroupModal: false, inboundGroupModalLoading: false, inboundGroupModalData: null });
    }

    fetchExtensionNumberDetail = async (extensionId) => {
        try {
            const reqBody = {
                start: 0, length: 10,
                search: { value: '' },
                columns: [], order: [{ column: 0, dir: 'desc' }],
                sorted: [{ id: 'id', desc: true }],
                filtered: [{ id: 'id', value: [parseInt(extensionId)] }]
            };
            const response = await this.props.ascAxios('post', 'ExtensionNumber/board', reqBody);
            if (response.data && response.data.rows && response.data.rows.length > 0) {
                return response.data.rows[0];
            } else {
                return null;
            }
        } catch (error) {
            console.error(this.props.langText.Message.ScriptNumberSettingInfoDiagram.extensionNumberDetailGetError, error);
            throw error;
        }
    }

    showExtensionNumberModal = async (extensionNumberData) => {
        try {
            let setData = {
                externalSettingTypeRadio: this.getSelectOption('external_setting_type', this.props.langText.SelectOption),
                inboundTypeRadio: this.getSelectOption('inbound_type', this.props.langText.SelectOption),
                inboundTypeSelect: this.getSelectOption('inbound_all_type', this.props.langText.SelectOption),
                monitoringFlgRadio: this.getSelectOption('monitoring_flg', this.props.langText.SelectOption),
                softphoneCodecTypeSelect: this.getSelectOption('softphone_codec_type', this.props.langText.SelectOption)
            };

            if (extensionNumberData) {
                setData.extension_number_id = extensionNumberData.id || "";
                setData.company_id = extensionNumberData.cm13_departments?.cm12_companies?.id || "";
                setData.company_name = extensionNumberData.cm13_departments?.cm12_companies?.company_name || "";
                setData.department_id = extensionNumberData.cm13_departments?.id || "";
                setData.department_name = extensionNumberData.cm13_departments?.department_name || "";
                setData.floor_flg = extensionNumberData.cm13_departments?.cm12_companies?.floor_flg === "Y";
                if (setData.floor_flg) {
                    setData.floor_id = extensionNumberData.cm13_departments?.id || "";
                    setData.floor_name = extensionNumberData.cm13_departments?.floor_name || "";
                }
                setData.extension_number = extensionNumberData.extension_number || "";
                setData.memo = extensionNumberData.memo || "";
                setData.type = extensionNumberData.type || "";
                setData.inbound_type = extensionNumberData.inbound_type || extensionNumberData.type || "";
                setData.monitoring_flg = extensionNumberData.monitoring_flg || "";
                setData.omni_phone_version = extensionNumberData.omni_phone_version || "";
                if (extensionNumberData.cm61_external_numbers) {
                    setData.external_number = extensionNumberData.cm61_external_numbers.id || "";
                    setData.external_number_name = extensionNumberData.cm61_external_numbers.display_number || "";
                }
                const cm28Groups = extensionNumberData.ct28_assign_inbound_group_infos && extensionNumberData.ct28_assign_inbound_group_infos.cm28_inbound_group_infos
                    ? extensionNumberData.ct28_assign_inbound_group_infos.cm28_inbound_group_infos : [];
                if (Array.isArray(cm28Groups) && cm28Groups.length > 0) {
                    const sortedGroups = [...cm28Groups].sort((a, b) => Number(a.id || 0) - Number(b.id || 0));
                    setData.inbound_group_id = sortedGroups.map(row => row.id);
                    setData.inboundGroupSelect = sortedGroups.map(row => ({ value: row.id, label: row.inbound_group_name }));
                }
            }

            setData.showExtensionNumberModal = true;
            setData.show = true;
            setData.showConfirm = false;
            setData.modalType = "read";

            this.setState({
                extensionNumberModalData: setData,
                showExtensionNumberModal: true,
                extensionNumberModalLoading: false,
                showExternalNumberModal: false,
                showInboundGroupModal: false,
                externalNumberModalLoading: false,
                inboundGroupModalLoading: false
            });
        } catch (error) {
            this.setState({ showExtensionNumberModal: false, extensionNumberModalLoading: false });
        }
    }

    handleExtensionNumberClick = async (event, extensionId) => {
        if (event) event.stopPropagation();
        if (!extensionId) return;
        this.setState({ extensionNumberModalLoading: true, showExtensionNumberModal: false, showInboundGroupModal: false, showExternalNumberModal: false });
        try {
            const data = await this.fetchExtensionNumberDetail(extensionId);
            if (data) {
                await this.showExtensionNumberModal(data);
            } else {
                this.setState({ extensionNumberModalLoading: false, showExtensionNumberModal: false });
            }
        } catch (error) {
            this.setState({ extensionNumberModalLoading: false, showExtensionNumberModal: false });
            this.showErrorObjectMesssage(error);
        }
    }

    closeExtensionNumberModal = () => {
        this.setState({ showExtensionNumberModal: false, extensionNumberModalLoading: false, extensionNumberModalData: null });
    }

    fetchBusinessCalendarDetail = async (calendarId) => {
        try {
            const response = await this.props.ascAxios('post', 'BusinessCalendar/modal', { id: calendarId });
            if (response.data && response.data.length > 0) {
                return response.data[0];
            }
            return null;
        } catch (error) {
            console.error('BusinessCalendar detail fetch error:', error);
            throw error;
        }
    }

    showBusinessCalendarModal = (calendarData) => {
        const langText = this.props.langText;
        const setData = {
            id: calendarData.id || '',
            business_calendar_name: calendarData.business_calendar_name || '',
            company_id: calendarData.cm12_companies?.id || '',
            company_name: calendarData.cm12_companies?.company_name || '',
            cm32_business_calendar_details: calendarData.cm32_business_calendar_details || [],
            cm33_business_calendar_irregular_infos: calendarData.cm33_business_calendar_irregular_infos || [],
            calendarUsingData: [],
            calendarValidationFlag: true,
            modalType: 'read',
            show: true,
            showRender: true,
            showConfirm: false,
            confirmMemo: '',
            confirmValue: '',
            isSetShowAddingConfirmationPopup: false
        };
        this.setState({
            businessCalendarModalData: setData,
            showBusinessCalendarModal: true,
            businessCalendarModalLoading: false,
            showExternalNumberModal: false,
            showInboundGroupModal: false,
            showExtensionNumberModal: false,
            externalNumberModalLoading: false,
            inboundGroupModalLoading: false,
            extensionNumberModalLoading: false
        });
    }

    handleCalendarClick = async (event, calendarId) => {
        if (event) event.stopPropagation();
        if (!calendarId) return;
        this.setState({
            businessCalendarModalLoading: true,
            showBusinessCalendarModal: false,
            showExternalNumberModal: false,
            showInboundGroupModal: false,
            showExtensionNumberModal: false
        });
        try {
            const data = await this.fetchBusinessCalendarDetail(calendarId);
            if (data) {
                this.showBusinessCalendarModal(data);
            } else {
                this.setState({ businessCalendarModalLoading: false, showBusinessCalendarModal: false });
            }
        } catch (error) {
            this.setState({ businessCalendarModalLoading: false, showBusinessCalendarModal: false });
            this.showErrorObjectMesssage(error);
        }
    }

    closeBusinessCalendarModal = () => {
        this.setState({ showBusinessCalendarModal: false, businessCalendarModalLoading: false, businessCalendarModalData: null });
    }

    closeNumberSettingInfoModal = () => {
        try {
            if (this.props && typeof this.props.propSetState === 'function') {
                this.props.propSetState({
                    showResourceMap: false,
                    resourceMapData: null,
                    resourceMapLoading: false
                });
            }
        } catch (e) { /* noop */ }
    }

    // 内線種類に応じたFaviアイコンを取得する関数
    getExtensionIcon = (extensionType) => {
        switch (extensionType) {
            case 'hardphone': return 'hardphone';
            case 'android': return 'android';
            case 'softphone': return 'softphone';
            default: return 'headphones';
        }
    }

    getBackNumbers = async (cm61_id) => {
        try {
            if (!cm61_id) return [];
            const result = await this.props.ascAxios("post", "ExternalNumber/getBackNumbers", { cm61_id });
            return result.data.map(data => ({ label: data.back_number, value: data.back_number }));
        } catch (error) {
            console.error(this.props.langText.Message.ScriptNumberSettingInfoDiagram.getBackNumberError, error);
            return [];
        }
    }

    getSoundName = (cm35_id) => {
        const { listOutSoundSelect } = this.state.externalNumberModalData || {};
        if (!listOutSoundSelect) return '';
        let returnData = '';
        listOutSoundSelect.forEach((row) => {
            if (row.value == cm35_id) { returnData = row.label; }
        });
        return returnData;
    }

    getInboundGroupName = () => {
        const child = this.state.extensionNumberModalData || {};
        const parent = this.props.state || {};
        const ids = Array.isArray(child.inbound_group_id) && child.inbound_group_id.length > 0 ? child.inbound_group_id : (parent.inbound_group_id || []);
        const options = Array.isArray(child.inboundGroupSelect) && child.inboundGroupSelect.length > 0 ? child.inboundGroupSelect : (parent.inboundGroupSelect || []);
        if (!ids || ids.length === 0 || !options || options.length === 0) return "";
        const idSet = new Set(ids.map(v => String(v)));
        const labels = options.filter(opt => idSet.has(String(opt.value))).sort((a, b) => Number(a.value || 0) - Number(b.value || 0)).map(opt => opt.label);
        return labels.join(",");
    }

    // ===== CSV出力 =====

    createCsvData = async () => {
        try {
            this.setState({ csvLoading: true });
            const { state, langText } = this.props;
            const { resourceMapData } = state;

            if (!resourceMapData) {
                console.error(langText.Message.ScriptNumberSettingInfoDiagram.csvEmptyResourceMapDataError);
                alert(langText.Message.ScriptNumberSettingInfoDiagram.csvNoDataToOutputAlert);
                this.setState({ csvLoading: false });
                return;
            }

            const csvHeader = [
                { label: langText.Body.ScriptNumberSettingInfoDiagram.csvHeaderExternalNumber, key: 'externalNumber' },
                { label: langText.Body.ScriptNumberSettingInfoDiagram.csvHeaderExternalNumberName, key: 'externalNumberName' },
                { label: langText.Body.ScriptNumberSettingInfoDiagram.csvHeaderBusinessCalendar, key: 'businessCalendar' },
                { label: langText.Body.ScriptNumberSettingInfoDiagram.csvHeaderScriptName, key: 'scriptName' },
                { label: langText.Body.ScriptNumberSettingInfoDiagram.csvHeaderIncomingBlockNumber, key: 'incomingBlockNumber' },
                { label: langText.Body.ScriptNumberSettingInfoDiagram.csvHeaderInboundGroup, key: 'inboundGroup' },
                { label: langText.Body.ScriptNumberSettingInfoDiagram.csvHeaderExtensionNumber, key: 'extensionNumber' },
                { label: langText.Body.ScriptNumberSettingInfoDiagram.csvHeaderExtensionName, key: 'extensionName' }
            ];

            const { businessCalendars = [], incomingBlocks = [], scriptName } = resourceMapData;
            const hasIncomingBlocks = incomingBlocks.length > 0;

            const csvData = [];

            // 右側のデータを構築
            const rightDataList = [];
            if (!hasIncomingBlocks) {
                const blockLabel = langText.Body.ScriptNumberSettingInfoDiagram.noIncomingBlockLabel;
                rightDataList.push({ incomingBlockNumber: blockLabel, inboundGroup: '', extensionNumber: '', extensionName: '' });
            } else {
                incomingBlocks.forEach(block => {
                    const blockTitle = block.block_title || "";
                    const incomingBlockNumber = blockTitle ? `#${block.block_number} ${blockTitle}` : `#${block.block_number}`;
                    if (block.inboundGroups.length === 0) {
                        rightDataList.push({ incomingBlockNumber, inboundGroup: '', extensionNumber: '', extensionName: '' });
                    } else {
                        block.inboundGroups.forEach(group => {
                            const canSee = this.canSeeByDepartment(group.department_id, group.department_prefix);
                            if (!canSee || group.extensionNumbers.length === 0) {
                                rightDataList.push({
                                    incomingBlockNumber,
                                    inboundGroup: group.inbound_group_name,
                                    extensionNumber: canSee ? '' : langText.Body.ScriptNumberSettingInfoDiagram.noReferencePlaceholder,
                                    extensionName: canSee ? '' : langText.Body.ScriptNumberSettingInfoDiagram.noReferencePlaceholder
                                });
                            } else {
                                group.extensionNumbers.forEach(ext => {
                                    rightDataList.push({
                                        incomingBlockNumber,
                                        inboundGroup: group.inbound_group_name,
                                        extensionNumber: (ext.unique_extension_number || ext.extension_number || '').replace(/[a-zA-Z]+$/, ''),
                                        extensionName: ext.memo || ''
                                    });
                                });
                            }
                        });
                    }
                });
            }

            // 左側のデータを構築
            const leftDataList = [];
            if (businessCalendars.length === 0) {
                leftDataList.push({ externalNumber: '', externalNumberName: '', businessCalendar: '' });
            } else {
                businessCalendars.forEach(cal => {
                    // テーブル描画と同じ権限フィルタリングを適用
                    const visibleExternals = cal.externalNumbers.filter(ext => {
                        if (this.props.getScopeGreaterEqual(GlobalConst.SCOPE_OBJECT.company)) return true;
                        if (!ext.departments || ext.departments.length === 0) return false;
                        return ext.departments.some(dept => this.canSeeByDepartment(dept.department_id, dept.department_prefix));
                    });
                    if (visibleExternals.length === 0) {
                        leftDataList.push({ externalNumber: '', externalNumberName: '', businessCalendar: cal.calendar_name });
                    } else {
                        visibleExternals.forEach(ext => {
                            leftDataList.push({ externalNumber: ext.display_number, externalNumberName: ext.memo || '', businessCalendar: cal.calendar_name });
                        });
                    }
                });
            }

            // 左側×右側の全組み合わせ（直積）で出力
            // → どの列でフィルターしても紐づく情報がすべて確認できる
            leftDataList.forEach(left => {
                rightDataList.forEach(right => {
                    csvData.push({
                        externalNumber: left.externalNumber,
                        externalNumberName: left.externalNumberName,
                        businessCalendar: left.businessCalendar,
                        scriptName,
                        incomingBlockNumber: right.incomingBlockNumber,
                        inboundGroup: right.inboundGroup,
                        extensionNumber: right.extensionNumber,
                        extensionName: right.extensionName
                    });
                });
            });

            if (csvData.length === 0) {
                console.error(langText.Message.ScriptNumberSettingInfoDiagram.csvEmptyResourceMapDataError);
                alert(langText.Message.ScriptNumberSettingInfoDiagram.csvNoDataToOutputAlert);
                this.setState({ csvLoading: false });
                return;
            }

            this.setState({ csvData, csvHeader, csvLoading: false }, () => {
                setTimeout(() => { this.csvLink.current.link.click(); }, 100);
            });
        } catch (error) {
            console.error(this.props.langText.Message.ScriptNumberSettingInfoDiagram.csvOutputError, error);
            this.setState({ csvLoading: false });
            alert(this.props.langText.Message.ScriptNumberSettingInfoDiagram.csvOutputFailedAlert);
        }
    }

    getCsvFileName = () => {
        const { state, langText } = this.props;
        const { resourceMapData } = state;
        const companyName = (resourceMapData && resourceMapData.companyName) ? resourceMapData.companyName : (this.props.userInfo.company_name || '');
        const nowTime = moment().utc().add(9, "h").format("YYYYMMDDHHmmss");
        return `${companyName}_${langText.Body.ScriptNumberSettingInfoDiagram.csvFileNameFixedWord}_${nowTime}.csv`;
    }

    // ===== render =====

    render() {
        const { state, langText, tableClass, connectionLineClass } = this.props;
        const { showResourceMap, resourceMapLoading, resourceMapData } = state;

        const modalState = { show: showResourceMap, modalType: "read", showConfirm: false };

        const wrappedPropSetState = (newState) => {
            if (newState && typeof newState.show !== 'undefined' && !newState.show) {
                this.setState({
                    showExternalNumberModal: false, showInboundGroupModal: false, showExtensionNumberModal: false,
                    externalNumberModalLoading: false, inboundGroupModalLoading: false, extensionNumberModalLoading: false,
                    externalNumberModalData: null, inboundGroupModalData: null, extensionNumberModalData: null
                });
                this.closeNumberSettingInfoModal();
            }
        };

        return (
            <div>
                <AscModal
                    state={modalState}
                    propSetState={wrappedPropSetState}
                    langText={{
                        ...langText,
                        Body: {
                            ...(langText?.Body || {}),
                            ReadTitle: langText.Body.ScriptNumberSettingInfoDiagram.title,
                            Cancel: langText.Body.Close
                        }
                    }}
                    dialogClassName="script-number-setting-info-diagram-modal non-scroll-bar"
                >
                    <Panel bsStyle="info">
                        <Panel.Heading>
                            <Panel.Title style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ fontWeight: "bold" }}>{langText.Body.ScriptNumberSettingInfoDiagram.titleInside}</span>
                                <Button
                                    className="table-button"
                                    bsStyle="default"
                                    bsSize="sm"
                                    onClick={this.createCsvData}
                                    disabled={this.state.csvLoading}
                                >
                                    {this.state.csvLoading
                                        ? langText.Body.ScriptNumberSettingInfoDiagram.csvLoading
                                        : langText.Body.ScriptNumberSettingInfoDiagram.csvButton}
                                </Button>
                            </Panel.Title>
                        </Panel.Heading>
                        <Panel.Body style={{ position: "relative" }}>
                            <Row>
                                <Col md={12}>
                                    {resourceMapLoading ? (
                                        <div className="text-center"><p>{langText.Table.LoadingText}</p></div>
                                    ) : resourceMapData ? (
                                        <div>
                                            {this.renderTableContent({ resourceMapData, tableClass, connectionLineClass })}
                                        </div>
                                    ) : (
                                        <div className="text-center"><p>{langText.Body.ScriptNumberSettingInfoDiagram.dataReadError}</p></div>
                                    )}
                                </Col>
                            </Row>
                        </Panel.Body>
                    </Panel>
                </AscModal>

                {this.state.showExternalNumberModal && (
                    <ExternalNumberModal
                        state={this.state.externalNumberModalData}
                        propSetState={(newState) => {
                            if (newState && newState.show === false) {
                                this.closeExternalNumberModal();
                            } else {
                                this.setState({ externalNumberModalData: { ...this.state.externalNumberModalData, ...newState } });
                            }
                        }}
                        langText={{
                            ...langText,
                            Body: {
                                ...langText.Body,
                                ReadTitle: langText.Body.ScriptNumberSettingInfoDiagram.titleExternal,
                                Cancel: langText.Body.Close
                            }
                        }}
                        onClick={this.closeExternalNumberModal}
                        getScopeGreaterEqual={this.props.getScopeGreaterEqual}
                        boardWidth={this.props.boardWidth}
                        getSoundName={this.getSoundName}
                    />
                )}

                {this.state.showInboundGroupModal && (
                    <InboundGroupModal
                        state={this.state.inboundGroupModalData}
                        propSetState={(newState) => {
                            if (newState && newState.show === false) {
                                this.closeInboundGroupModal();
                            } else {
                                this.setState({ inboundGroupModalData: { ...this.state.inboundGroupModalData, ...newState } });
                            }
                        }}
                        langText={{
                            ...langText,
                            Body: {
                                ...langText.Body,
                                ReadTitle: langText.Body.ScriptNumberSettingInfoDiagram.titleInboundGroup,
                                Cancel: langText.Body.Close
                            }
                        }}
                        onClick={this.closeInboundGroupModal}
                        getScopeGreaterEqual={this.props.getScopeGreaterEqual}
                    />
                )}

                {this.state.showExtensionNumberModal && (
                    <ExtensionNumberModal
                        state={this.state.extensionNumberModalData}
                        propSetState={(newState) => {
                            if (newState && newState.show === false) {
                                this.closeExtensionNumberModal();
                            } else {
                                this.setState({ extensionNumberModalData: { ...this.state.extensionNumberModalData, ...newState } });
                            }
                        }}
                        langText={{
                            ...langText,
                            Body: {
                                ...langText.Body,
                                ReadTitle: langText.Body.ScriptNumberSettingInfoDiagram.titleExtensionNumber,
                                Cancel: langText.Body.Close
                            }
                        }}
                        onClick={this.closeExtensionNumberModal}
                        getScopeGreaterEqual={this.props.getScopeGreaterEqual}
                        getinboundGroupName={this.getInboundGroupName}
                    />
                )}

                {this.state.showBusinessCalendarModal && this.state.businessCalendarModalData && (
                    <BusinessCalendarModal
                        state={this.state.businessCalendarModalData}
                        propSetState={(newState) => {
                            if (newState && newState.show === false) {
                                this.closeBusinessCalendarModal();
                            } else {
                                this.setState({ businessCalendarModalData: { ...this.state.businessCalendarModalData, ...newState } });
                            }
                        }}
                        langText={{
                            ...langText,
                            Body: {
                                ...langText.Body,
                                ReadTitle: langText.Body.ScriptNumberSettingInfoDiagram.titleBusinessCalendar || langText.Body.ScriptNumberSettingInfoDiagram.columnBusinessCalendar,
                                Cancel: langText.Body.Close
                            }
                        }}
                        onClick={this.closeBusinessCalendarModal}
                        getScopeGreaterEqual={this.props.getScopeGreaterEqual}
                        boardWidth={this.props.boardWidth}
                        charaLimit={this.props.charaLimit}
                        reactContainerPath="/BusinessCalendar"
                        isTargetShowAddingConfirmationPopup={false}
                        currentPermission={this.props.currentPermission}
                        dayString={{
                            [BusinessCalendarType.DAY.SUN]: langText.Body.Sunday,
                            [BusinessCalendarType.DAY.MON]: langText.Body.Monday,
                            [BusinessCalendarType.DAY.TUE]: langText.Body.Tuesday,
                            [BusinessCalendarType.DAY.WED]: langText.Body.Wednesday,
                            [BusinessCalendarType.DAY.THU]: langText.Body.Thursday,
                            [BusinessCalendarType.DAY.FRI]: langText.Body.Friday,
                            [BusinessCalendarType.DAY.SAT]: langText.Body.Saturday,
                            [BusinessCalendarType.DAY.HOLI]: langText.Body.Holiday,
                        }}
                        actionString={{
                            [BusinessCalendarType.ACTION.INBOUND]: langText.Body.Inbound,
                            [BusinessCalendarType.ACTION.BUSY]: langText.Body.Busy,
                            [BusinessCalendarType.ACTION.TRANSFER]: langText.Body.ExternalTransfer,
                            [BusinessCalendarType.ACTION.VOICE]: langText.Body.PlayVoice,
                            [BusinessCalendarType.ACTION.SCRIPT]: langText.Body.Script,
                            [BusinessCalendarType.ACTION.ANSWERING_MACHINE]: langText.Body.AnsweringMachine,
                            [BusinessCalendarType.ACTION.AUTO_RECEPTION]: langText.Body.AutoReception,
                        }}
                        businessString={{
                            [BusinessCalendarType.BUSINESS.START]: langText.Body.BusinessDay,
                            [BusinessCalendarType.BUSINESS.END]: langText.Body.DayOff,
                        }}
                    />
                )}

                <CSVLink
                    data={this.state.csvData}
                    headers={this.state.csvHeader}
                    filename={this.getCsvFileName()}
                    className="csv-download-link-format hidden"
                    ref={this.csvLink}
                />
            </div>
        );
    }
}

export default ScriptNumberSettingInfoDiagram;
